import { fetchAuthSession } from 'aws-amplify/auth';
import * as Sentry from '@sentry/vue';
import type {
  Member,
  EmailActivity,
  SearchParams,
  SearchMembersResponse,
  AuditLogResponse,
  BatchMemberInput,
  BatchCreateResponse,
  TransactionsResponse,
  TransactionsSummary,
  TransactionLedgerEntry,
  CreateTransactionRequest,
  Club1855Level,
  Club1855GivingSource,
  Club1855ReconciliationResponse,
} from '@/types';

export interface ReferenceDataItem {
  PK: string;
  SK: string;
  type: string;
  code: string;
  label: string;
  sortOrder?: number;
  active?: boolean;
  category?: string;
  // Only meaningful for type '1855' — see backend/shared/types.ts ReferenceData.
  monthlyAmount?: number;
  annualAmount?: number;
}

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Bounds any request that would otherwise hang forever with no error and no
// way for the UI to recover — observed via a session recording where a
// member browsing inside Facebook's in-app browser (a WebView, not Safari)
// hit an infinite loading spinner on the Family Tree section: the fetch()
// never resolved or rejected, so nothing ever cleared the loading state.
const REQUEST_TIMEOUT_MS = 20000;

function raceWithTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new DOMException('Request timed out', 'AbortError')), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function friendlyHttpMessage(status: number, serverMessage?: string): string {
  // 400 and 409 backend messages are typically specific validation feedback — pass them through.
  if (serverMessage && (status === 400 || status === 409)) return serverMessage;
  switch (status) {
    case 401: return 'Your session has expired. Please sign in again.';
    case 403: return "You don't have permission to do that.";
    case 404: return 'That record was not found.';
    case 429: return 'Too many requests. Please wait a moment and try again.';
    case 500:
    case 502:
    case 503: return 'Something went wrong on our end. Please try again in a moment.';
    default:  return serverMessage || 'Something went wrong. Please try again.';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const controller = new AbortController();

  let response: Response;
  try {
    // Wrapped together so a hang in either the auth-token lookup (Amplify's
    // own network call) or the fetch itself is caught by the same timeout —
    // aborting the controller only cancels the fetch, not a stuck Amplify call.
    response = await raceWithTimeout(
      (async () => {
        const authHeaders = await getAuthHeaders();
        return fetch(`${API_BASE}${endpoint}`, {
          ...options,
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
            ...options.headers,
          },
        });
      })(),
      REQUEST_TIMEOUT_MS
    );
  } catch (networkError) {
    if ((networkError as Error)?.name === 'AbortError') {
      controller.abort(); // no-op if it already settled, stops a still-pending fetch otherwise
      Sentry.captureException(networkError, { extra: { endpoint, timeoutMs: REQUEST_TIMEOUT_MS } });
      throw new Error('This is taking longer than expected. Please check your connection and try again.');
    }
    // fetch() itself threw — offline, DNS failure, CORS, etc.
    Sentry.captureException(networkError, { extra: { endpoint } });
    throw new Error("Couldn't connect to the server. Check your internet connection and try again.");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const technicalError = new Error(body.error || `HTTP ${response.status}`);
    if (response.status !== 404) {
      Sentry.captureException(technicalError, { extra: { endpoint, status: response.status, body } });
    }
    throw new Error(friendlyHttpMessage(response.status, body.error));
  }

  return response.json();
}

export const api = {
  // Auth endpoints (no auth required)
  async claimAccount(email: string) {
    return request<{ message: string }>('/auth/claim-account', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async verifyAccount(email: string, code: string, password: string) {
    return request<{ message: string; memberId: number }>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code, password }),
    });
  },

  // Member endpoints
  async searchMembers(params: SearchParams): Promise<SearchMembersResponse> {
    const queryParams = new URLSearchParams();

    if (params.query) queryParams.set('query', params.query);
    if (params.status) {
      const statusValue = Array.isArray(params.status)
        ? params.status.join(',')
        : params.status;
      queryParams.set('status', statusValue);
    }
    if (params.graduationYear) queryParams.set('graduationYear', params.graduationYear.toString());
    if (params.pledgeClass) queryParams.set('pledgeClass', params.pledgeClass);
    if (params.state) queryParams.set('state', params.state);
    if (params.includeDeceased === false) queryParams.set('includeDeceased', 'false');
    if (params.deceasedOnly) queryParams.set('deceasedOnly', 'true');
    if (params.excludeStatuses !== undefined) {
      queryParams.set('excludeStatuses', params.excludeStatuses.join(','));
    }
    queryParams.set('limit', (params.limit ?? 2000).toString());
    if (params.lastKey) queryParams.set('lastKey', params.lastKey);

    return request<SearchMembersResponse>(`/members?${queryParams.toString()}`);
  },

  async searchSuggest(query: string): Promise<{
    query: string;
    results: Array<{
      memberId: number;
      displayName: string;
      firstName: string;
      lastName: string;
      photoUrl?: string | null;
      graduationYear?: number;
      matchType: 'first' | 'last';
    }>;
  }> {
    return request(`/members/search?q=${encodeURIComponent(query)}`);
  },

  async getMember(memberId: number): Promise<{ member: Member }> {
    return request<{ member: Member }>(`/members/${memberId}`);
  },

  async getLittleBrothers(memberId: number): Promise<{ members: Member[]; count: number }> {
    return request<{ members: Member[]; count: number }>(`/members/${memberId}/littlebrothers`);
  },

  async getOfficers(): Promise<{ members: Member[] }> {
    return request<{ members: Member[] }>('/members/officers');
  },

  async getMyProfile(): Promise<{ member: Member }> {
    return request<{ member: Member }>('/members/me');
  },

  async getMyGivingHistory(): Promise<TransactionsResponse> {
    return request<TransactionsResponse>('/members/me/transactions');
  },

  // 1855 Club giving — level catalog + the caller's current giving state,
  // bundled in one call so the displayed price can't drift from what
  // checkout would actually charge (see docs/1855-club-stripe-plan.md).
  async getMyGiving(): Promise<{
    levels: { name: string; club1855Level: Club1855Level; monthlyAmount: number; annualAmount: number }[];
    currentGiving: {
      club1855Level?: Club1855Level;
      club1855GivingSource?: Club1855GivingSource;
      hasActiveSubscription: boolean;
    };
  }> {
    return request('/members/me/giving');
  },

  async createCheckoutSession(data: {
    level?: string;
    mode: 'monthly' | 'annual' | 'one_time';
    customAmount?: number;
  }): Promise<{ url: string }> {
    return request('/donate/checkout-session', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Cancels the caller's own active 1855 Club subscription immediately —
  // used to let a member switch giving levels via cancel-then-restart
  // rather than stacking a second subscription (see GivingPage.vue).
  async cancelSubscription(): Promise<{ message: string }> {
    return request('/donate/cancel-subscription', { method: 'POST' });
  },

  async updateMember(
    memberId: number,
    changes: Partial<Member>
  ): Promise<{ member?: Member; changeId?: string; status: string }> {
    return request(`/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify({ changes }),
    });
  },

  // Officer transition — admin-only list of current officer holders
  async getAdminOfficers(): Promise<{ members: Member[] }> {
    return request<{ members: Member[] }>('/admin/officers');
  },

  async adminUpdateMember(memberId: number, changes: Partial<Member>, notes?: string): Promise<{ member: Member }> {
    return request(`/admin/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify({ changes, ...(notes ? { notes } : {}) }),
    });
  },

  async createMember(data: {
    firstName: string;
    lastName: string;
    email: string;
    suffix?: string;
    nickname?: string;
    graduationYear?: number;
    pledgeClass?: string;
    status?: string;
    bigBrotherId?: number;
  }): Promise<{ message: string; member: Member }> {
    return request('/admin/members', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async batchCreateMembers(members: BatchMemberInput[]): Promise<BatchCreateResponse> {
    return request<BatchCreateResponse>('/admin/members/batch', {
      method: 'POST',
      body: JSON.stringify({ members }),
    });
  },

  async deleteMember(memberId: number, reason?: string): Promise<{ message: string }> {
    return request(`/admin/members/${memberId}`, {
      method: 'DELETE',
      body: reason ? JSON.stringify({ reason }) : undefined,
    });
  },

  async clearBounceFlag(memberId: number): Promise<{ message: string }> {
    return request(`/admin/members/${memberId}/bounce-flag`, { method: 'DELETE' });
  },

  async getUndeliverableMembers(): Promise<{
    members: {
      memberId: number;
      fullName: string;
      email: string;
      pledgeClass: string;
      graduationYear: number | null;
      emailBounceSource: string;
      bounceTimestamp: string;
    }[];
    count: number;
  }> {
    return request('/admin/members/undeliverable');
  },

  async getUsers(): Promise<{
    users: {
      memberId: number;
      firstName: string;
      lastName: string;
      email: string | undefined;
      photoUrl: string | null;
      roles: string[];
      accountClaimedAt?: string;
      flashReport: 'none' | 'daily' | 'weekly';
    }[];
    count: number;
  }> {
    return request('/admin/users');
  },

  async updateRoles(memberId: number, roles: string[]): Promise<{ message: string; roles: string[] }> {
    return request(`/admin/members/${memberId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ roles }),
    });
  },

  async setFlashReport(memberId: number, frequency: 'none' | 'daily' | 'weekly'): Promise<{ message: string }> {
    return request(`/admin/members/${memberId}/flash-report`, {
      method: 'PUT',
      body: JSON.stringify({ frequency }),
    });
  },

  async getMemberRole(memberId: number): Promise<{ roles: string[] }> {
    return request(`/admin/members/${memberId}/role`, {
      method: 'GET',
    });
  },

  async sendActivationEmail(memberId: number): Promise<{ message: string }> {
    return request(`/admin/members/${memberId}/send-activation`, {
      method: 'POST',
    });
  },

  async sendPasswordReset(memberId: number): Promise<{ message: string }> {
    return request(`/admin/members/${memberId}/send-reset`, {
      method: 'POST',
    });
  },

  // Admin stats/dashboard
  async getAdminStats(): Promise<{
    memberStats: {
      total: number;
      byStatus: Record<string, { count: number; label: string }>;
      accountsClaimed: number;
      accountsUnclaimed: number;
      withPhotos: number;
      deceased: number;
      activeClub1855Members: number;
    };
    distributions: {
      byGraduationDecade: Record<string, number>;
      byState: Record<string, number>;
      byPledgeClass: Record<string, number>;
      topEmployers: { name: string; count: number }[];
    };
    recentActivity: {
      entries: { memberId: number; memberName?: string; changeType: string; timestamp: string; notes?: string }[];
      recentClaims: { memberId: number; name: string; date: string }[];
    };
  }> {
    return request('/admin/stats');
  },

  // Audit log
  async getAuditLog(params?: { limit?: number; lastKey?: string }): Promise<AuditLogResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.lastKey) queryParams.set('lastKey', params.lastKey);
    const qs = queryParams.toString();
    return request<AuditLogResponse>(`/admin/audit${qs ? `?${qs}` : ''}`);
  },

  async getMemberAuditLog(memberId: number): Promise<AuditLogResponse> {
    return request<AuditLogResponse>(`/admin/members/${memberId}/audit`);
  },

  async getMemberTransactions(memberId: number): Promise<TransactionsResponse> {
    return request<TransactionsResponse>(`/admin/members/${memberId}/transactions`);
  },

  // Transaction ledger (treasurer view)
  async getAllTransactions(params?: {
    limit?: number;
    lastKey?: string;
    program?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    sortAscending?: boolean;
  }): Promise<TransactionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.lastKey) queryParams.set('lastKey', params.lastKey);
    if (params?.program) queryParams.set('program', params.program);
    if (params?.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params?.toDate) queryParams.set('toDate', params.toDate);
    if (params?.search) queryParams.set('search', params.search);
    if (params?.sortAscending) queryParams.set('sortAscending', 'true');
    const qs = queryParams.toString();
    return request<TransactionsResponse>(`/admin/transactions${qs ? `?${qs}` : ''}`);
  },

  async getTransactionsSummary(params?: {
    program?: string;
    fromDate?: string;
    toDate?: string;
    memberId?: number;
  }): Promise<TransactionsSummary> {
    const queryParams = new URLSearchParams();
    if (params?.program) queryParams.set('program', params.program);
    if (params?.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params?.toDate) queryParams.set('toDate', params.toDate);
    if (params?.memberId) queryParams.set('memberId', params.memberId.toString());
    const qs = queryParams.toString();
    return request<TransactionsSummary>(`/admin/transactions/summary${qs ? `?${qs}` : ''}`);
  },

  async getClub1855Reconciliation(): Promise<Club1855ReconciliationResponse> {
    return request<Club1855ReconciliationResponse>('/admin/club1855-reconciliation');
  },

  async createTransaction(data: CreateTransactionRequest): Promise<{ entry: TransactionLedgerEntry }> {
    return request('/admin/transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async exportTransactionsCSV(params?: {
    program?: string;
    fromDate?: string;
    toDate?: string;
    memberId?: number;
  }): Promise<Blob> {
    const authHeaders = await getAuthHeaders();
    const queryParams = new URLSearchParams();
    if (params?.program) queryParams.set('program', params.program);
    if (params?.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params?.toDate) queryParams.set('toDate', params.toDate);
    if (params?.memberId) queryParams.set('memberId', params.memberId.toString());

    const qs = queryParams.toString();
    const url = `${API_BASE}/admin/transactions/export${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, { headers: authHeaders });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    return response.blob();
  },

  async getEmailActivity(memberId: number): Promise<{ activities: EmailActivity[]; memberName: string }> {
    return request(`/admin/members/${memberId}/email-activity`);
  },

  async getRecentEmailActivity(days: number = 7): Promise<{ activities: EmailActivity[]; days: number }> {
    return request(`/admin/email-activity?days=${days}`);
  },

  // Export
  async exportCSV(params?: SearchParams): Promise<Blob> {
    const authHeaders = await getAuthHeaders();
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.query) queryParams.set('query', params.query);
      if (params.status) {
        const statusValue = Array.isArray(params.status)
          ? params.status.join(',')
          : params.status;
        queryParams.set('status', statusValue);
      }
      if (params.graduationYear) queryParams.set('graduationYear', params.graduationYear.toString());
      if (params.pledgeClass) queryParams.set('pledgeClass', params.pledgeClass);
      if (params.state) queryParams.set('state', params.state);
      if (params.includeDeceased === false) queryParams.set('includeDeceased', 'false');
    }

    const qs = queryParams.toString();
    const url = `${API_BASE}/admin/export${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, {
      headers: authHeaders,
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  },

  // Photo upload
  async getPhotoUploadUrl(
    memberId: number,
    contentType: string,
    fileExtension: string,
    fileSize?: number
  ): Promise<{ uploadUrl: string; photoKey: string; expiresAt: string }> {
    return request('/photos/upload-url', {
      method: 'POST',
      body: JSON.stringify({ memberId, contentType, fileExtension, fileSize }),
    });
  },

  async getDeceasedDefaultUploadUrl(): Promise<{
    uploadUrl: string;
    photoKey: string;
    expiresAt: string;
  }> {
    return request('/photos/upload-deceased-default', {
      method: 'POST',
    });
  },

  async uploadPhoto(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error('Photo upload failed');
    }
  },

  // Reference data
  async getReferenceData(): Promise<{
    statuses: ReferenceDataItem[];
    levels: ReferenceDataItem[];
    offices: ReferenceDataItem[];
    employers: ReferenceDataItem[];
  }> {
    return request('/reference');
  },

  async putReferenceData(data: {
    type: string;
    code: string;
    label: string;
    sortOrder?: number;
    active?: boolean;
    category?: string;
    monthlyAmount?: number;
    annualAmount?: number;
  }): Promise<{ message: string; item: ReferenceDataItem }> {
    return request('/admin/reference', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteReferenceData(type: string, code: string): Promise<{ message: string }> {
    return request(`/admin/reference/${type}/${code}`, {
      method: 'DELETE',
    });
  },

  // Site config
  async getConfig(key: string): Promise<{ key: string; value: boolean }> {
    return request(`/admin/config/${key}`);
  },

  async putConfig(key: string, value: boolean): Promise<{ key: string; value: boolean; message: string }> {
    return request(`/admin/config/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

};
